import React, { useState, useEffect } from "react";
import ApiService from "../services/ApiService";
import "./PricingPlanManagement.css";

const PricingPlanManagement = () => {
  const [pricingPlans, setPricingPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [pricingPlan, setPricingPlan] = useState(null);
  const [carTypesWithPricing, setCarTypesWithPricing] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    creditsGiven: 0,
    oneTimeCreditsGiven: 0,
    validity: 30,
    infiniteValidity: false,
    active: true,
  });

  useEffect(() => {
    fetchPricingPlans();
  }, []);

  const fetchPricingPlans = async () => {
    try {
      setLoading(true);
      const response = await ApiService.getPricingPlans(true); // Include inactive
      setPricingPlans(response.data || []);
      setError("");
    } catch (error) {
      console.error("Error fetching pricing plans:", error);
      setError("Failed to fetch pricing plans");
    } finally {
      setLoading(false);
    }
  };
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "infiniteValidity") {
      setFormData((prev) => ({
        ...prev,
        infiniteValidity: checked,
        validity: checked ? -1 : 30, // Set validity to -1 for infinite, 30 for default
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]:
          type === "checkbox"
            ? checked
            : type === "number"
            ? Number(value)
            : value,
      }));
    }
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError("Name is required");
      return;
    }

    try {
      setLoading(true);

      // Prepare submission data with correct validity value
      const submissionData = {
        ...formData,
        validity: formData.infiniteValidity ? -1 : formData.validity,
      };

      // Remove infiniteValidity from submission data as it's not needed by backend
      delete submissionData.infiniteValidity;

      if (editingPlan) {
        await ApiService.updatePricingPlan(editingPlan.id, submissionData);
      } else {
        await ApiService.createPricingPlan(submissionData);
      }

      await fetchPricingPlans();
      closeModal();
      setError("");
    } catch (error) {
      console.error("Error saving pricing plan:", error);
      setError(error.message || "Failed to save pricing plan");
    } finally {
      setLoading(false);
    }
  };
  const handleEdit = (plan) => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name,
      description: plan.description || "",
      creditsGiven: plan.creditsGiven,
      oneTimeCreditsGiven: plan.oneTimeCreditsGiven,
      validity: plan.validity === -1 ? 30 : plan.validity, // Show 30 if infinite, actual value otherwise
      infiniteValidity: plan.validity === -1, // Check if validity is infinite
      active: plan.active,
    });
    setShowEditModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this pricing plan?")) {
      try {
        setLoading(true);
        await ApiService.deletePricingPlan(id);
        await fetchPricingPlans();
        setError("");
      } catch (error) {
        console.error("Error deleting pricing plan:", error);
        setError(error.message || "Failed to delete pricing plan");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleRestore = async (id) => {
    try {
      setLoading(true);
      await ApiService.restorePricingPlan(id);
      await fetchPricingPlans();
      setError("");
    } catch (error) {
      console.error("Error restoring pricing plan:", error);
      setError(error.message || "Failed to restore pricing plan");
    } finally {
      setLoading(false);
    }
  };

  const handleManagePricing = async (plan) => {
    try {
      setLoading(true);
      const response = await ApiService.getPricingPlanWithCarTypes(plan.id);
      setPricingPlan(response.data.pricingPlan);
      setCarTypesWithPricing(response.data.carTypesWithPricing || []);
      setShowPricingModal(true);
      setError("");
    } catch (error) {
      console.error("Error fetching pricing plan with car types:", error);
      setError(error.message || "Failed to fetch pricing plan details");
    } finally {
      setLoading(false);
    }
  };

  const handlePricingChange = (index, field, value) => {
    setCarTypesWithPricing((prev) =>
      prev.map((item, idx) =>
        idx === index
          ? { ...item, pricing: { ...item.pricing, [field]: value } }
          : item
      )
    );
  };

  const handleSavePricing = async () => {
    try {
      setLoading(true);
      const carTypePricing = carTypesWithPricing.map((item) => ({
        carTypeId: item.carType.id,
        price: parseFloat(item.pricing.price) || 0,
        enabled: item.pricing.enabled,
      }));

      await ApiService.bulkUpdatePricing(pricingPlan.id, carTypePricing);
      setShowPricingModal(false);
      setError("");
    } catch (error) {
      console.error("Error saving pricing:", error);
      setError(error.message || "Failed to save pricing");
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    setShowPricingModal(false);
    setEditingPlan(null);
    setPricingPlan(null);
    setCarTypesWithPricing([]);
    setFormData({
      name: "",
      description: "",
      creditsGiven: 0,
      oneTimeCreditsGiven: 0,
      validity: 30,
      infiniteValidity: false,
      active: true,
    });
    setError("");
  };

  if (loading && pricingPlans.length === 0) {
    return (
      <div className="pricing-plan-management">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading pricing plans...</p>
        </div>
      </div>
    );
  }
  return (
    <div className="pricing-plan-management dark-theme">
      <div className="pricing-plan-header">
        <h2>Pricing Plan Management</h2>
        <button
          className="add-pricing-plan-btn"
          onClick={() => setShowAddModal(true)}
          disabled={loading}
        >
          + Add Pricing Plan
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="pricing-plan-grid">
        {pricingPlans.map((plan) => (
          <div
            key={plan.id}
            className={`pricing-plan-card ${!plan.active ? "inactive" : ""}`}
          >
            <div className="pricing-plan-info">
              <h3>{plan.name}</h3>
              {plan.description && (
                <p className="description">{plan.description}</p>
              )}
              <div className="plan-details">
                <div className="detail-item">
                  <span className="label">Credits Given:</span>
                  <span className="value">{plan.creditsGiven}</span>
                </div>
                <div className="detail-item">
                  <span className="label">One-time Credits:</span>
                  <span className="value">{plan.oneTimeCreditsGiven}</span>
                </div>{" "}
                <div className="detail-item">
                  <span className="label">Validity:</span>
                  <span className="value">
                    {plan.validity === -1
                      ? "Infinite"
                      : `${plan.validity} days`}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="label">Status:</span>
                  <span
                    className={`status ${plan.active ? "active" : "inactive"}`}
                  >
                    {plan.active ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>
              <div className="created-at">
                Created: {new Date(plan.createdAt).toLocaleDateString()}
              </div>
            </div>
            <div className="pricing-plan-actions">
              <button
                className="btn btn-primary"
                onClick={() => handleManagePricing(plan)}
                disabled={loading}
              >
                Manage Pricing
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => handleEdit(plan)}
                disabled={loading}
              >
                Edit
              </button>
              {plan.active ? (
                <button
                  className="btn btn-danger"
                  onClick={() => handleDelete(plan.id)}
                  disabled={loading}
                >
                  Delete
                </button>
              ) : (
                <button
                  className="btn btn-success"
                  onClick={() => handleRestore(plan.id)}
                  disabled={loading}
                >
                  Restore
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {pricingPlans.length === 0 && !loading && (
        <div className="no-data">
          <p>No pricing plans found.</p>
          <button
            className="add-pricing-plan-btn"
            onClick={() => setShowAddModal(true)}
          >
            Create your first pricing plan
          </button>
        </div>
      )}

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {editingPlan ? "Edit Pricing Plan" : "Add New Pricing Plan"}
              </h3>
              <button className="close-btn" onClick={closeModal}>
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-group">
                <label>Plan Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter plan name"
                  required
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter plan description"
                  rows="3"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Credits Given</label>
                  <input
                    type="number"
                    name="creditsGiven"
                    value={formData.creditsGiven}
                    onChange={handleInputChange}
                    min="0"
                  />
                </div>
                <div className="form-group">
                  <label>One-time Credits</label>
                  <input
                    type="number"
                    name="oneTimeCreditsGiven"
                    value={formData.oneTimeCreditsGiven}
                    onChange={handleInputChange}
                    min="0"
                  />
                </div>
              </div>{" "}
              <div className="form-row">
                <div className="form-group">
                  <label>Validity</label>
                  <div className="validity-controls">
                    <input
                      type="number"
                      name="validity"
                      value={formData.validity}
                      onChange={handleInputChange}
                      min="1"
                      disabled={formData.infiniteValidity}
                      placeholder="Days"
                    />
                    <label className="checkbox-label infinite-validity">
                      <input
                        type="checkbox"
                        name="infiniteValidity"
                        checked={formData.infiniteValidity}
                        onChange={handleInputChange}
                      />
                      Infinite Validity
                    </label>
                  </div>
                </div>
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="active"
                      checked={formData.active}
                      onChange={handleInputChange}
                    />
                    Active
                  </label>
                </div>
              </div>
              {error && <div className="error-message">{error}</div>}
              <div className="form-actions">
                <button
                  type="button"
                  onClick={closeModal}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn btn-primary"
                >
                  {loading
                    ? "Saving..."
                    : editingPlan
                    ? "Update Plan"
                    : "Create Plan"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Pricing Management Modal */}
      {showPricingModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div
            className="modal-content large"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>Manage Pricing - {pricingPlan?.name}</h3>
              <button className="close-btn" onClick={closeModal}>
                ×
              </button>
            </div>
            <div className="pricing-management">
              <div className="pricing-table">
                <div className="table-header">
                  <div>Car Type</div>
                  <div>Price</div>
                  <div>Status</div>
                </div>
                {carTypesWithPricing.map((item, index) => (
                  <div key={item.carType.id} className="table-row">
                    <div className="car-type-name">{item.carType.name}</div>
                    <div className="price-input">
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={item.pricing.price || ""}
                        onChange={(e) =>
                          handlePricingChange(index, "price", e.target.value)
                        }
                        placeholder="0.00"
                      />
                    </div>
                    <div className="status-toggle">
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={item.pricing.enabled}
                          onChange={(e) =>
                            handlePricingChange(
                              index,
                              "enabled",
                              e.target.checked
                            )
                          }
                        />
                        <span className="toggle-slider"></span>
                      </label>
                      <span
                        className={`status-text ${
                          item.pricing.enabled ? "enabled" : "disabled"
                        }`}
                      >
                        {item.pricing.enabled ? "Enabled" : "Disabled"}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              {error && <div className="error-message">{error}</div>}
              <div className="form-actions">
                <button
                  type="button"
                  onClick={closeModal}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSavePricing}
                  disabled={loading}
                  className="btn btn-primary"
                >
                  {loading ? "Saving..." : "Save Pricing"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PricingPlanManagement;
